import { useState } from 'react'
import type { FormEvent, ReactElement } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import { cn } from '../../lib/cn'
import { errorMessage } from '../../lib/api'
import { submitPasswordReset } from '../../lib/auth'
import { useT } from '../../lib/i18n'
import { passwordStrength } from '../../lib/validate'
import { Button, ButtonLink } from '../../components/ui/Button'
import { Field } from '../../components/ui/Field'
import { Input } from '../../components/ui/Input'
import { InlineError } from '../../components/ui/ErrorState'
import { EmptyState } from '../../components/ui/EmptyState'
import { AuthLayout } from './AuthLayout'
import { STRENGTH_KEY, newPasswordProblem } from './validation'

const Reset = (): ReactElement => {
  const t = useT()
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const token = params.get('token') ?? ''

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [passwordError, setPasswordError] = useState<string | undefined>(undefined)
  const [confirmError, setConfirmError] = useState<string | undefined>(undefined)
  const [formError, setFormError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState(false)

  const strength = passwordStrength(password)

  const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault()
    const fault = newPasswordProblem(password)
    const mismatch = password !== confirm
    setPasswordError(fault === null ? undefined : t(fault))
    setConfirmError(mismatch ? t('auth.validation.passwordMismatch') : undefined)
    setFormError(null)
    if (fault !== null || mismatch) return

    setSubmitting(true)
    try {
      await submitPasswordReset(token, password)
      setDone(true)
    } catch (error) {
      setFormError(errorMessage(error))
    } finally {
      setSubmitting(false)
    }
  }

  if (!token) {
    return (
      <AuthLayout title={t('auth.reset.incompleteTitle')}>
        <EmptyState
          title={t('auth.reset.missingTokenTitle')}
          description={t('auth.reset.missingTokenBody')}
          action={
            <ButtonLink to="/forgot" variant="primary">
              {t('auth.reset.requestNew')}
            </ButtonLink>
          }
        />
      </AuthLayout>
    )
  }

  if (done) {
    return (
      <AuthLayout title={t('auth.reset.doneTitle')} subtitle={t('auth.reset.doneBody')}>
        <Button
          variant="primary"
          size="lg"
          fullWidth
          onClick={() => {
            void navigate('/login')
          }}
        >
          {t('auth.reset.goToSignIn')}
        </Button>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      title={t('auth.reset.title')}
      subtitle={t('auth.reset.subtitle')}
      footer={
        <p className="t-small text-ink-mute">
          <Link
            to="/login"
            className="ring-focus font-semibold text-brass-deep underline underline-offset-2"
          >
            {t('auth.common.backToSignIn')}
          </Link>
        </p>
      }
    >
      <form
        noValidate
        onSubmit={(event) => {
          void onSubmit(event)
        }}
        className="flex flex-col gap-4"
      >
        {formError ? <InlineError>{formError}</InlineError> : null}

        <Field label={t('auth.reset.newPassword')} error={passwordError} required>
          <Input
            type="password"
            name="password"
            value={password}
            autoComplete="new-password"
            autoFocus
            onChange={(event) => {
              setPassword(event.target.value)
            }}
          />
        </Field>

        {password ? (
          <div className="flex items-center gap-2" aria-live="polite">
            <span className="flex flex-1 gap-1">
              {[1, 2, 3].map((step) => (
                <span
                  key={step}
                  className={cn(
                    'h-1 flex-1 rounded-full',
                    strength >= step ? 'bg-brass' : 'bg-sunken',
                  )}
                />
              ))}
            </span>
            <span className="t-mono text-ink-mute">{t(STRENGTH_KEY[strength])}</span>
          </div>
        ) : null}

        <Field label={t('auth.reset.repeatPassword')} error={confirmError} required>
          <Input
            type="password"
            name="confirmPassword"
            value={confirm}
            autoComplete="new-password"
            onChange={(event) => {
              setConfirm(event.target.value)
            }}
          />
        </Field>

        <Button type="submit" variant="primary" size="lg" fullWidth loading={submitting}>
          {t('auth.reset.submit')}
        </Button>
      </form>
    </AuthLayout>
  )
}

export default Reset
